/**
 * Shared utilities for transforming rate data between different formats
 * Used by both table and chart components
 */

import { DateHelpers } from '@/helpers/date-helpers';
import type {
  ChartDataPoint,
  ColumnType,
  CompetitorInfo,
  RateData,
  RateEntry,
  RateShopData,
  RateType,
} from '../types/rate-shop-types';
import { getMostRecentRateChange } from './rate-change-utils';

/**
 * Transforms structured table data into chart-consumable format
 */
export function transformDataForChart(data: RateShopData): {
  chartData: ChartDataPoint[];
  competitorInfo: CompetitorInfo[];
} {
  const { headers, rows } = data;

  if (!Array.isArray(headers) || headers.length === 0) {
    return { chartData: [], competitorInfo: [] };
  }

  // Find stay_date column (should be first column)
  const dateColumnIndex = headers.findIndex(
    (header) => header.dataKey === 'stay_date' || header.title.toLowerCase().includes('date'),
  );

  if (dateColumnIndex === -1) {
    return { chartData: [], competitorInfo: [] };
  }

  // Get competitor columns (exclude stay_date and dow columns)
  const competitorColumns = headers
    .map((header, index) => ({ header, index }))
    .filter(({ header, index }) => index !== dateColumnIndex && !header.dataKey.includes('dow'));

  // Extract competitor info
  const competitorInfo: CompetitorInfo[] = competitorColumns.map(({ header }) => {
    let type: ColumnType = 'competitor';

    if (header.title === 'CLIENT' || header.dataKey.includes('rates.0')) {
      type = 'client';
    } else if (header.title.includes('Price Index')) {
      type = 'price_index';
    }

    return {
      name: header.title,
      type,
      dataKey: header.dataKey,
      tooltip: header.tooltip,
    };
  });

  // Transform rows into chart data points
  const chartData: ChartDataPoint[] = rows
    .filter((row) => row[dateColumnIndex]?.value) // Only rows with valid dates
    .map((row) => {
      const dateCell = row[dateColumnIndex];
      const stayDate = dateCell.stayDate || (dateCell.value as string);

      const dataPoint: ChartDataPoint = {
        date: stayDate,
        displayDate: DateHelpers.formatDate(stayDate, 'MMM D'),
      };

      // Add competitor rates to data point
      competitorColumns.forEach(({ header, index }) => {
        const cell = row[index];
        let value: number | null = null;
        let isSoldOut = false;

        if (cell && cell.value !== null && cell.value !== undefined) {
          // For rate data, the value should be numeric
          if (typeof cell.value === 'number') {
            if (cell.value < 1) {
              // Sold out - try to get previous rate from changes
              const changes = cell.meta?.data?.changes;
              const previousRate = changes ? getMostRecentRateChange(changes) : null;
              value = previousRate;
              isSoldOut = true;
            } else {
              value = cell.value;
            }
          } else if (typeof cell.value === 'string') {
            // Try to parse string values (remove $ and parse)
            const parsed = parseFloat(cell.value.toString().replace(/[$,]/g, ''));
            if (!Number.isNaN(parsed)) {
              if (parsed < 1) {
                // Sold out - try to get previous rate from changes
                const changes = cell.meta?.data?.changes;
                const previousRate = changes ? getMostRecentRateChange(changes) : null;
                value = previousRate;
                isSoldOut = true;
              } else {
                value = parsed;
              }
            }
          }
        }

        dataPoint[header.dataKey] = value;
        dataPoint[`${header.dataKey}_soldOut`] = isSoldOut;
      });

      return dataPoint;
    })
    .filter((dataPoint) => dataPoint.date); // Ensure valid date

  return { chartData, competitorInfo };
}

/**
 * Validates that rate data contains expected fields
 */
export function validateRateData(rateData: RateData): boolean {
  return (
    rateData &&
    typeof rateData.stay_date === 'string' &&
    Array.isArray(rateData.rates) &&
    rateData.rates.every(
      (rate: RateEntry) =>
        rate.competitor_name &&
        (typeof rate.value === 'number' || typeof rate.best_flex === 'number'),
    )
  );
}

/**
 * Extracts rate value based on rate type
 */
export function extractRateValue(rate: RateEntry, rateType: RateType): number | null {
  if (!rate) return null;

  if (rateType === 'best-flex') {
    // Check if best_flex is a boolean (true means cancellable) or a number (rate value)
    if (typeof rate.best_flex === 'number') {
      return rate.best_flex;
    } else if (rate.best_flex === true) {
      return rate.value || null; // If best_flex is true, use the value
    }
    return rate.value || null; // Fallback to regular value
  }

  return rate.value || null;
}

/**
 * Filters out invalid or incomplete rate data
 */
export function cleanRateData(data: RateData[]): RateData[] {
  if (!data || data.length === 0) return [];

  return data.filter(validateRateData);
}

/**
 * Calculates Y-axis domain for chart based on visible data
 */
export function calculateYAxisDomain(
  chartData: ChartDataPoint[],
  visibleCompetitors: string[],
): [number, number] {
  if (visibleCompetitors.length === 0) return [0, 300];

  let minRate = Number.POSITIVE_INFINITY;
  let maxRate = Number.NEGATIVE_INFINITY;

  chartData.forEach((dataPoint) => {
    visibleCompetitors.forEach((competitor) => {
      const rate = dataPoint[competitor];
      if (typeof rate === 'number') {
        if (rate < minRate) minRate = rate;
        if (rate > maxRate) maxRate = rate;
      }
    });
  });

  // Calculate 20% padding based on data range
  const range = maxRate - minRate;
  const paddingPercent = 0.2;
  let padding = range * paddingPercent;

  // Ensure minimum padding of 20 for small ranges
  if (padding < 20) {
    padding = 20;
  }

  const yMin = Math.max(0, Math.round(minRate - padding));
  const yMax = Math.round(maxRate + padding);

  return [yMin, yMax];
}

/**
 * Generates chart colors configuration
 */
export function generateChartColors(
  competitors: string[],
): Record<string, { label: string; color: string }> {
  const colors = [
    'hsl(var(--chart-1))',
    'hsl(var(--chart-2))',
    'hsl(var(--chart-3))',
    'hsl(var(--chart-4))',
    'hsl(var(--chart-5))',
    'hsl(220 70% 50%)',
    'hsl(280 70% 50%)',
    'hsl(340 70% 50%)',
    'hsl(40 70% 50%)',
    'hsl(160 70% 50%)',
  ];

  const config: Record<string, { label: string; color: string }> = {};
  competitors.forEach((competitor, index) => {
    config[competitor] = {
      label: competitor,
      color: colors[index % colors.length],
    };
  });

  return config;
}

/**
 * Formats pagination-based date range label for display
 */
export function getDateRangeLabel(currentPage: number, daysPerPage: number): string {
  const start = (currentPage - 1) * daysPerPage + 1;
  const end = currentPage * daysPerPage;
  return `Days ${start}-${end}`;
}
